import { I18nPluralPipe } from '@angular/common';
import { Component, computed, effect, inject, input, linkedSignal, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideListFilter } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { BadgeDirective } from '@shared/directives/badge.directive';
import { INSTITUTS_MÉTIER } from '@shared/models/instituts-métier';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmIconImports } from '@spartan-ng/helm/icon';

import { Site } from '../../../administration/gestion-sites/model/site.model';
import { SiteStore } from '../../../administration/gestion-sites/store/site.store';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { CardEtapeComponent } from '../../../catalogue-national/card-etape/card-etape.component';
import { FiltresCatalogueNationalComponent } from '../../../catalogue-national/filtres-catalogue-national/filtres-catalogue-national.component';
import { Étape } from '../../../référentiel-métier-parcours/models/étape.model';
import { ParcoursStore } from '../../../référentiel-métier-parcours/store/parcours.store';
import { ComputeFormationsParImPipe } from '../../pipes/compute-formations-par-im.pipe';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-selection-offre-regionale',
  imports: [
    HlmIconImports,
    HlmButtonImports,
    HlmCardImports,
    ExpansionPanel,
    TrierParPipe,
    CardEtapeComponent,
    ReactiveFormsModule,
    HlmCheckboxImports,
    ChipComponent,
    I18nPluralPipe,
    ComputeFormationsParImPipe,
    RouterModule,
    BadgeDirective,
  ],
  templateUrl: './sélection-offre-régionale.component.html',
  providers: [provideIcons({ lucideListFilter, lucideArrowLeft })],
})
export class SélectionOffreRégionaleComponent {
  readonly #référentielStore = inject(ReferentielStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #parcoursStore = inject(ParcoursStore);
  readonly #siteStore = inject(SiteStore);
  readonly #fb = inject(FormBuilder);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);

  readonly site = input.required<Site>();
  readonly cartographie = input.required<Cartographie>();

  readonly #referentiels = linkedSignal<Référentiel[]>(() => this.#référentielStore.getFilteredReferentiels());

  readonly #institutsMetier = Array.from(INSTITUTS_MÉTIER, ([key, value]) => ({ key: key, nom: `${key} - ${value}` }));
  readonly data: WritableSignal<Institut[]> = signal([]);
  readonly nbFiltres = computed(() => Object.values(this.#référentielStore.filtres() ?? {}).filter((x) => !!x).length);

  readonly form = this.#fb.group({
    etapes: [[]],
  });

  protected readonly formationMapping: { [k: string]: string } = {
    '=0': '# formation',
    '=1': '# formation',
    other: '# formations',
  };

  constructor() {
    effect(() => {
      this.#parcoursStore.charger({ idCartographie: this.cartographie().id });
      this.#référentielStore.chargerParCartographie(this.cartographie().id);
    });

    effect(() => {
      const site = this.site();

      this.#siteStore.trouverParId({ id: site.id });

      this.etapes.setValue(site.etapes);
    });

    effect(() => {
      const parcours = this.#parcoursStore.entities();
      const référentiels = this.#referentiels();

      const result = this.#institutsMetier
        .map((im) => ({
          ...im,
          referentiels: référentiels
            .filter((x) => x.equipeResponsable === im.key)
            .map((referentiel) => ({
              ...referentiel,
              etapes:
                parcours.find((x) => x.idReferentiel === referentiel.id)?.noeuds.filter((x) => !x.estPointDepart) ?? [],
            })),
        }))
        .filter((x) =>
          this.#référentielStore.filtres()?.equipeResponsable
            ? x.key === this.#référentielStore.filtres()!.equipeResponsable
            : true,
        );
      this.data.set(result);
    });
  }

  isChecked = (idEtape: string) => (this.etapes.value as Étape[]).map((x) => x.id).includes(idEtape);

  isCheckedInstitut = (institut: Institut) =>
    institut.referentiels.length > 0 &&
    institut.referentiels.every((référentiel) => this.isCheckedReferentiel(institut.key, référentiel.id));

  isIndeterminateInstitut = (institut: Institut) =>
    !this.isCheckedInstitut(institut) &&
    institut.referentiels.some(
      (référentiel) =>
        this.isIndeterminateReferentiel(institut.key, référentiel.id) ||
        this.isCheckedReferentiel(institut.key, référentiel.id),
    );

  isCheckedReferentiel = (acronymeInstitut: string, idReferentiel: string) => {
    const étapesReferentiel =
      this.data()
        .find((x) => x.key === acronymeInstitut)
        ?.referentiels.find((x) => x.id === idReferentiel)?.etapes ?? [];
    if (étapesReferentiel.length === 0) return false;

    const étapesSélectionnées = this.etapes.value as Étape[];

    return étapesReferentiel.every((étapeRéférentiel) =>
      étapesSélectionnées.map((x) => x.id).includes(étapeRéférentiel.id),
    );
  };

  isIndeterminateReferentiel = (acronymeInstitut: string, idReferentiel: string) => {
    const étapesReferentiel =
      this.data()
        .find((x) => x.key === acronymeInstitut)
        ?.referentiels.find((x) => x.id === idReferentiel)?.etapes ?? [];
    if (étapesReferentiel.length === 0) return false;

    const étapesSélectionnées = this.etapes.value as Étape[];

    return (
      !this.isCheckedReferentiel(acronymeInstitut, idReferentiel) &&
      étapesReferentiel.some((étapeRéférentiel) => étapesSélectionnées.map((x) => x.id).includes(étapeRéférentiel.id))
    );
  };

  onChangeInstitut(institut: Institut) {
    const nouvellesÉtapes: Étape[] = [];
    const étapesInstitut = institut.referentiels.flatMap((x) => x.etapes) ?? [];
    const étapesSélectionnées = this.etapes.value as Étape[];

    if (this.isCheckedInstitut(institut)) {
      // Si l'institut est coché, on retire toutes les étapes de cet institut
      nouvellesÉtapes.push(...étapesSélectionnées.filter((x) => !étapesInstitut.map((x) => x.id).includes(x.id)));
    } else if (this.isIndeterminateInstitut(institut)) {
      // Sinon, si l'institut est partiellement sélectionné, on rajoute les étapes manquantes
      nouvellesÉtapes.push(
        ...étapesSélectionnées,
        ...étapesInstitut.filter((x) => !étapesSélectionnées.map((x) => x.id).includes(x.id)),
      );
    } else {
      // Sinon, on ajoute toutes les étapes
      nouvellesÉtapes.push(...étapesSélectionnées, ...étapesInstitut);
    }
    this.etapes.setValue(nouvellesÉtapes);
  }

  onChangeReferentiel(acronymeInstitut: string, idReferentiel: string) {
    const nouvellesÉtapes: Étape[] = [];
    const étapesReferentiel =
      this.data()
        .find((x) => x.key === acronymeInstitut)
        ?.referentiels.find((x) => x.id === idReferentiel)?.etapes ?? [];
    const étapesSélectionnées = this.etapes.value as Étape[];

    if (this.isCheckedReferentiel(acronymeInstitut, idReferentiel)) {
      // Si l'institut est coché, on retire toutes les étapes de cet institut
      nouvellesÉtapes.push(...étapesSélectionnées.filter((x) => !étapesReferentiel.map((x) => x.id).includes(x.id)));
    } else if (this.isIndeterminateReferentiel(acronymeInstitut, idReferentiel)) {
      // Sinon, si l'institut est partiellement sélectionné, on rajoute les étapes manquantes
      nouvellesÉtapes.push(
        ...étapesSélectionnées,
        ...étapesReferentiel.filter((x) => !étapesSélectionnées.map((x) => x.id).includes(x.id)),
      );
    } else {
      // Sinon, on ajoute toutes les étapes
      nouvellesÉtapes.push(...étapesSélectionnées, ...étapesReferentiel);
    }
    this.etapes.setValue(nouvellesÉtapes);
  }

  onClickEtape(etape: Étape) {
    const values = this.etapes.value as Étape[];
    const index = values.findIndex((x) => x.id === etape.id);

    if (index === -1) {
      values.push(etape);
    } else {
      values.splice(index, 1);
    }

    this.etapes.setValue(values);
  }

  onSubmit() {
    const étapes = this.etapes.value as Étape[];

    this.#siteStore.modifier({
      site: { ...this.site(), etapes: étapes },
      next: () => this.#router.navigate(['..'], { relativeTo: this.#route }),
    });
  }

  openFiltres() {
    const cartographie = this.cartographie();
    if (cartographie) {
      this.#sidepanelService.setComponentToDisplay(
        'Filtrer',
        FiltresCatalogueNationalComponent,
        `Vous pouvez filtrer les éléments qui seront affichés dans le catalogue national pour faciliter votre recherche.`,
        { idCartographie: cartographie.id },
      );
      this.#sidepanelService.open();
    }
  }

  get etapes() {
    return this.form.get('etapes') as AbstractControl;
  }
}

interface Institut {
  key: string;
  nom: string;
  referentiels: (Référentiel & { etapes: Étape[] })[];
}
